<template>
  <div class="win" :style="styleObj" :class="classObj">
    <div class="win__bar">
      <div class="win__heading">
        <h2 v-if="title" class="win__title">{{ title }}</h2>
      </div>
      <div class="win__btns">
        <button
          class="win__btn win__btn--close"
          title="Close"
          @click.prevent="close"
        >
          Close
        </button>
      </div>
    </div>
    <div class="win__content">
      <slot />
    </div>
  </div>
</template>

<script>
import wait from 'waait'
import debounce from 'lodash/debounce'

export default {
  name: 'Window',

  props: {
    title: {
      default: 'wack',
      type: String
    }
  },

  data() {
    return {
      opened: false,
      x: 0,
      y: 0
    }
  },

  computed: {
    classObj() {
      return {
        'is-opened': this.opened
      }
    },

    styleObj() {
      return {
        left: `${this.x}px`,
        top: `${this.y}px`
      }
    }
  },

  created() {
    this.debouncedResize = debounce(this.resize, 250)
  },

  mounted() {
    this.$nextTick(this.layout)
    window.addEventListener('resize', this.debouncedResize, { passive: true })
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.debouncedResize, {
      passive: true
    })
  },

  methods: {
    async layout() {
      const buffer = 40
      const w = this.$el.clientWidth
      const h = this.$el.clientHeight
      const winW = window.innerWidth - buffer
      const winH = window.innerHeight - buffer
      const randX = Math.floor(Math.random() * (winW - w))
      const randY = Math.floor(Math.random() * (winH - h))
      this.x = randX
      this.y = randY
      if (!this.opened) {
        await wait(50)
        this.opened = true
      }
    },

    close() {
      this.$emit('close')
    },

    resize() {
      this.layout()
    }
  }
}
</script>

<style>
:root {
  --win-corner-size: 1.5rem;
  --win-side-size: 0.75rem;
}

.win {
  background-color: theme('colors.winGray');
  border: 3px solid theme('colors.winGray');
  box-shadow: 2px 2px 0 0 theme('colors.black');
  display: flex;
  flex-direction: column;
  max-height: 100%;
  max-width: 100%;
  opacity: 0;
  position: fixed;
  transition: opacity 0.25s;
  width: 250px;
  will-change: opacity;
}

.win.is-opened {
  opacity: 1;
}

.win__bar {
  align-items: center;
  background-color: theme('colors.winBlue');
  color: theme('colors.white');
  display: flex;
  flex: 0 0 auto;
  flex-wrap: nowrap;
  justify-content: space-between;
  line-height: 1;
  margin-bottom: 3px;
  padding: 4px 3px 4px 6px;
  touch-action: none;
  user-select: none;
}

.win__heading {
  @apply mr-1;
  align-items: center;
  display: flex;
  flex: 0 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.win__title {
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}

.win__btns {
  white-space: nowrap;
}

.win__btn {
  @apply font-blank;
  background-color: theme('colors.winGray');
  background-image: url('../assets/images/window/close.svg');
  background-position: 50%;
  background-repeat: no-repeat;
  background-size: 18px 18px;
  box-shadow: 1px 1px 0 0 theme('colors.black');
  display: inline-block;
  height: 20px;
  margin-left: 3px;
  position: relative;
  width: 20px;
  z-index: 3;
}

.win__content {
  background-color: theme('colors.white');
  color: theme('colors.black');
  flex: 1 1 auto;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  position: relative;
}

@screen sm {
  .win {
    width: auto;
  }
}

@screen md {
  .win__btn {
    background-size: 16px 16px;
    height: 18px;
    margin-left: 2px;
    width: 19px;
  }
}
</style>
